import React from 'react';
import { ArrowLeft, Clock, User, Building2, Chrome, Shield, Smartphone, Bot, HardDrive, CheckCircle, AlertTriangle, ArrowRight, Monitor } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const ArticlePage = ({ article, category, onBack }) => {
  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Article non trouvé</h1>
            <p className="text-gray-600 mb-8">L'article que vous cherchez n'existe pas ou n'est plus disponible.</p>
            <Link to="/">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                Retour à l'accueil
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to format date in European format
  const formatDateEuropean = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Get category icon and colors
  const getCategoryData = () => {
    switch (category) {
      case 'Office 365':
        return {
          icon: Building2,
          colors: 'from-blue-600 to-indigo-600',
          bgColors: 'from-blue-50 to-indigo-50',
          textColor: 'text-blue-600'
        };
      case 'Google Workspace':
        return {
          icon: Chrome,
          colors: 'from-green-600 to-emerald-600',
          bgColors: 'from-green-50 to-emerald-50',
          textColor: 'text-green-600'
        };
      case 'Sécurité':
        return {
          icon: Shield,
          colors: 'from-red-600 to-orange-600',
          bgColors: 'from-red-50 to-orange-50',
          textColor: 'text-red-600'
        };
      case 'Appareils Mobiles':
        return {
          icon: Smartphone,
          colors: 'from-purple-600 to-pink-600',
          bgColors: 'from-purple-50 to-pink-50',
          textColor: 'text-purple-600'
        };
      case 'Intelligence Artificielle':
        return {
          icon: Bot,
          colors: 'from-indigo-600 to-purple-600',
          bgColors: 'from-indigo-50 to-purple-50',
          textColor: 'text-indigo-600'
        };
      case 'Synology':
        return {
          icon: HardDrive,
          colors: 'from-teal-600 to-cyan-600',
          bgColors: 'from-teal-50 to-cyan-50',
          textColor: 'text-teal-600'
        };
      case 'Systèmes OS':
        return {
          icon: Monitor,
          colors: 'from-slate-600 to-gray-600',
          bgColors: 'from-slate-50 to-gray-50',
          textColor: 'text-slate-600'
        };
      default:
        return {
          icon: Building2,
          colors: 'from-indigo-600 to-purple-600',
          bgColors: 'from-indigo-50 to-purple-50',
          textColor: 'text-indigo-600'
        };
    }
  };

  const categoryData = getCategoryData();
  const CategoryIcon = categoryData.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb />
        </div>
      </div>

      {/* Hero Banner */}
      <section className={`relative overflow-hidden bg-gradient-to-r ${categoryData.colors}`}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-4xl">
            <div className="flex items-center mb-6">
              <Badge className="bg-white bg-opacity-20 text-white border-white border-opacity-30 mr-4">
                {category}
              </Badge>
              <Badge className="bg-white bg-opacity-20 text-white border-white border-opacity-30">
                {article.type}
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-xl text-white text-opacity-90 mb-6 leading-relaxed">
              {article.description}
            </p>
            <div className="flex items-center text-white text-opacity-80">
              <Clock className="h-5 w-5 mr-2" />
              <span>Publié le {formatDateEuropean(article.date)}</span>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 right-10 w-20 h-20 bg-white bg-opacity-10 rounded-full animate-bounce"></div>
        <div className="absolute bottom-20 left-10 w-16 h-16 bg-white bg-opacity-10 rounded-full animate-pulse"></div>
      </section>

      {/* Article Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Introduction Section */}
        <section className={`bg-gradient-to-br ${categoryData.bgColors} rounded-2xl p-8 mb-12`}>
          <div className="flex items-center mb-6">
            <div className={`p-3 rounded-xl bg-gradient-to-r ${categoryData.colors} mr-4`}>
              <CategoryIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Points clés de l'article
              </h2>
              <p className="text-gray-600">Les informations essentielles à retenir</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Information vérifiée</h3>
                <p className="text-gray-700 text-sm">Contenu validé par nos experts techniques</p>
              </div>
            </div>
            <div className="flex items-start">
              <AlertTriangle className="h-6 w-6 text-amber-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Mise en pratique</h3>
                <p className="text-gray-700 text-sm">Conseils directement applicables en entreprise</p>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-8">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                {article.content || 'Le contenu de cet article sera bientôt disponible. Nous travaillons actuellement sur la rédaction de guides complets et détaillés pour vous accompagner dans vos défis digitaux.'}
              </p>
              
              {/* Mock content sections */}
              <h3 className={`text-xl font-bold mb-4 ${categoryData.textColor}`}>Contexte et enjeux</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Cette section présente le contexte général et les enjeux liés au sujet traité dans l'article.
              </p>
              
              <h3 className={`text-xl font-bold mb-4 ${categoryData.textColor}`}>Solutions recommandées</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Découvrez nos recommandations d'experts pour répondre efficacement aux problématiques identifiées.
              </p>
              
              <h3 className={`text-xl font-bold mb-4 ${categoryData.textColor}`}>Mise en œuvre pratique</h3>
              <p className="text-gray-700 leading-relaxed">
                Guide étape par étape pour implémenter les solutions dans votre environnement professionnel.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center mt-12">
          <Button 
            onClick={() => {
              if (onBack) {
                onBack();
              } else {
                window.history.back();
              }
            }}
            className={`bg-gradient-to-r ${categoryData.colors} hover:shadow-lg text-white font-bold px-8 py-4 rounded-full text-lg transform hover:scale-105 transition-all duration-200`}
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour aux articles
          </Button>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ArticlePage;